/**
 * Component Registry for Weidner Apartment Homes Theme
 * 
 * Extends the base via6 theme registry with Weidner Apartment Homes specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-via6/registry';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { SitemapContent } from './components/SitemapContent';
import { PricingVerbiage } from './components/PricingVerbiage';
import { ApplyNow } from './components/ApplyNow';
import { FutureResidentsClub } from './components/FutureResidentsClub';

/**
 * Weidner Apartment Homes specific component overrides.
 *
 * `Header` is forked from `@vibesites/theme-via6` solely so the navigation
 * picks up Weidner's `pages.json` overrides. The base via6 `_HeaderBase.tsx`
 * imports `pages.json` via a static `from '../pages.json'`, which resolves to
 * the via6 theme's own pages.json and never sees child-theme overrides. The
 * forked copies in `./components/_HeaderBase.tsx` and `./components/Header.tsx`
 * import both the base and the Weidner pages.json and shallow-merge them
 * (child wins per alias) so the rendered nav reflects the merged hierarchy
 * (Features parent dropdown, Gallery & Tours renamed, Virtual Tour nested,
 * etc.). Visual behavior is otherwise identical to the base.
 *
 * `Footer` is a copy-source fork of the base via6 Footer that
 * retargets the base's two generic "program logo" slots (rendered
 * small beneath the community logo) to the canonical Weidner-brand
 * "Weidner Cares" and "Love Notes" badges, with default image URLs
 * and link targets pre-wired so a fresh Weidner property shows them
 * automatically. See `./components/Footer.tsx` for the wrap-vs-fork
 * rationale.
 *
 * `SitemapContent` is a copy-source fork of the base via6 component with the
 * grid of selectable page-link buttons removed, leaving only the editable
 * page heading.
 *
 * `PricingVerbiage` is adapted from the Weidner Preferred theme — a
 * custom_pricing_verbiage richtext strip shown above ApartmentSync on
 * Floor Plans when that field has content.
 *
 * `ApplyNow` is adapted from Homestead Theme — two action tiles (start
 * application + call for help) on /apply-now, matching ResidentAccess.
 *
 * `FutureResidentsClub` replaces the How It Works image with an RRAC
 * waitlist mount point (`span.rrac_waitlist_layout`).
 */
const weidnerapartmenthomesOverrides: Partial<ComponentRegistry> = {
  Header,
  Footer,
  SitemapContent,
  PricingVerbiage,
  ApplyNow,
  FutureResidentsClub,
};

/**
 * Final merged registry for Weidner Apartment Homes properties.
 * Inherits all components from the via6 theme, with Weidner Apartment Homes overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...weidnerapartmenthomesOverrides,
};

export default registry;
